import { Component, OnInit } from '@angular/core';
import { EoptionsService } from 'src/app/services/eoptions.service';
import { ToastrService } from 'ngx-toastr';
import { getDisplayedPages } from 'src/app/lib/pagination';
import { OptionTradeSocket } from 'src/app/app.module';
import { toFixed, toFixedDown, truncateDecimals } from 'src/app/lib/roundOf';

@Component({
    selector: 'app-option-history',
    templateUrl: './option-history.component.html',
    styleUrls: ['./option-history.component.css']
})
export class OptionHistoryComponent implements OnInit {
    activeTab: string = 'open-orders';
    orders: any[] = [];
    isLoading = false;
    pagination = {
        page: 1,
        limit: 10,
        totalPages: 0,
        totalItems: 0
    };
    displayedPages: number[] = [];
    toFixedDown = toFixedDown
    constructor(private eoptionsService: EoptionsService,
        private toastr: ToastrService,
        private socket: OptionTradeSocket) { }

    ngOnInit(): void {
        this.fetchOrders();
        this.socket.on('order', this.fetchWSOrders.bind(this))
        this.socket.on('optionMarkPrice', (data: any) => this.updateMarkPrice(data));
    }
    fetchWSOrders() {
        if (this.activeTab !== 'positions') {
            this.fetchOrders();
        } else if (this.activeTab == 'positions') {
            this.fetchPositions();
        }
    }

    updateMarkPrice(socketRes: any) {
        if (this.activeTab !== 'positions') return;
        // 3. O(1) Instant Lookup (No looping)
        const order = this.orders.find((o: any) => o.symbol === socketRes.symbol);
        let data = socketRes.data

        if (order) {
            let openOrderValue = order.entryPrice * Math.abs(order.quantity)
            let currentOrderValue = data.mp * Math.abs(order.quantity)
            let unrealizedPNL = currentOrderValue - openOrderValue
            let markValue = currentOrderValue
            // 4. Update properties directly (Mutable)
            order.markPrice = data.mp;
            order.unrealizedPNL = unrealizedPNL;
            order.markValue = markValue;
            this.orders = [...this.orders]

            // 5. Trigger detection effectively
            // If using Default Change Detection, this happens automatically.
            // If using OnPush strategy (Recommended for performance), use this:
        }

        // REMOVED: this.orders = [...this.orders] 
        // Never spread an array inside a high-frequency socket event!
    }

    fetchOrders() {
        if (this.activeTab == 'positions') return;
        this.isLoading = true;
        const params: any = {
            page: this.pagination.page,
            limit: this.pagination.limit,
            status: this.getStatusForTab(this.activeTab)
        };

        this.eoptionsService.getOrderHistory(params).subscribe({
            next: (res: any) => {
                this.isLoading = false;
                // Assuming response structure has 'data' (array) and 'count' (total items)
                // Adjust based on actual API response if different. 
                // Using fallback if count is missing to avoid breaking.
                if (res.status) {
                    this.orders = res.data || [];
                    this.pagination.totalItems = res.count || this.orders.length;
                    this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.limit);
                    this.displayedPages = getDisplayedPages(this.pagination.page, this.pagination.totalPages, 5);
                } else {
                    this.orders = [];
                    this.pagination.totalItems = 0;
                    this.pagination.totalPages = 0;
                    this.displayedPages = [];
                }
            },
            error: (err) => {
                this.isLoading = false;
                console.error(err);
                this.toastr.error('Failed to fetch orders');
            }
        });
    }

    fetchPositions() {
        if (this.activeTab !== 'positions') return;
        this.isLoading = true;
        const params: any = {
            page: this.pagination.page,
            limit: this.pagination.limit,
            status: this.getStatusForTab(this.activeTab)
        };

        this.eoptionsService.getPositionHistory(params).subscribe({
            next: (res: any) => {
                this.isLoading = false;
                // Assuming response structure has 'data' (array) and 'count' (total items)
                // Adjust based on actual API response if different. 
                // Using fallback if count is missing to avoid breaking.
                if (res.status) {
                    this.orders = res.data || [];
                    this.pagination.totalItems = res.count || this.orders.length;
                    this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.limit);
                    this.displayedPages = getDisplayedPages(this.pagination.page, this.pagination.totalPages, 5);
                } else {
                    this.orders = [];
                    this.pagination.totalItems = 0;
                    this.pagination.totalPages = 0;
                    this.displayedPages = [];
                }
            },
            error: (err) => {
                this.isLoading = false;
                console.error(err);
                this.toastr.error('Failed to fetch orders');
            }
        });
    }

    getStatusForTab(tab: string): string {
        switch (tab) {
            case 'open-orders': return 'open-orders'; // or ['open', 'PARTIALLY_FILLED', 'PENDING_NEW'] depends on backend
            case 'order-history': return 'closed-orders'; // or 'FILLED,CANCELLED,REJECTED'
            case 'trade-history': return 'trade-history';
            case 'positions': return 'positions';
            default: return 'all';
        }
    }

    previousPage() {
        if (this.pagination.page > 1) {
            this.pagination.page--;
            this.fetchOrders();
        }
    }

    nextPage() {
        if (this.pagination.page < this.pagination.totalPages) {
            this.pagination.page++;
            this.fetchOrders();
        }
    }

    goToPage(page: number) {
        if (page !== this.pagination.page && page >= 1 && page <= this.pagination.totalPages) {
            this.pagination.page = page;
            this.fetchOrders();
        }
    }

    cancelOrder(order: any) {
        if (!confirm('Are you sure you want to cancel this order?')) return;

        this.eoptionsService.cancelOrder(order.symbol, order.orderId).subscribe({
            next: (res) => {
                this.toastr.success('Order Cancelled Successfully');
                this.fetchOrders(); // Refresh list
            },
            error: (err) => {
                console.error('Cancel Failed:', err);
                const msg = err.error?.message || err.message || 'Error cancelling order';
                this.toastr.error(msg);
            }
        });
    }

    setActiveTab(tab: string) {
        this.activeTab = tab;
        this.pagination.page = 1; // Reset to first page
        if (tab == 'positions') {
            this.fetchPositions();
        } else {
            this.fetchOrders()
        }
        this.orders = [];
    }

    get filteredOrders() {
        return this.orders;
    }
}
